import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    TextField, 
    Button,  
    CircularProgress, 
    Checkbox, 
    MenuItem,
    FormControlLabel, 
    Select, 
    FormControl, 
    InputLabel,
} from '@mui/material';
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const initialValues = {   
    supplyStandardName: '',    
    sectors: [],    
    departureAirports: [],    
    products: [],    
    minBlockTime: 0,
    sortOrder: 0,
    isActive: true,
    multiplier: 1,
};

const SupplystandardPage = () => {
    useAuthCheck();
    const { supplyStandardId } = useParams();    
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); 

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [products, setProducts] = useState([]);
    const [airports, setAirports] = useState([]);
    const [airportPairs, setAirportPairs] = useState([]);
    const [supplyStandards, setSupplyStandards] = useState([]);
    const [selectedSupplyStandardId, setSelectedSupplyStandardId] = useState('');

    const [formValues, setFormValues] = useState(initialValues);
    

    const fetchData = useCallback(async (url, setter, errorMsg) => {
        try {
            const { data } = await api.get(url);
            setter(data);
        } catch (error) {
            setError(error);
            toast.error(error.response?.data?.message || errorMsg);
        }
    }, []);         

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setError(null);
                setLoading(true);

                const urls = [
                    { url: '/airports', setter: setAirports, errorMsg: 'Failed to fetch airports!' },
                    { url: '/airportpairs', setter: setAirportPairs, errorMsg: 'Failed to fetch airport pairs!' },
                    { url: '/products/active', setter: setProducts, errorMsg: 'Failed to fetch products!' },
                    { url: '/supplystandards', setter: setSupplyStandards, errorMsg: 'Failed to fetch supply standards!' },
                ];
                if (supplyStandardId) {
                    urls.push({ 
                        url: `/supplystandards/${supplyStandardId}`,
                        setter: (data) => setFormValues({ ...initialValues, ...data }),
                        errorMsg: 'Failed to fetch supply standard!',
                    });
                }
        
                await Promise.all(urls.map(({ url, setter, errorMsg }) => fetchData(url, setter, errorMsg)));
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };  

        fetchAllData();
    }, [fetchData, supplyStandardId]);

    
    const validationSchema = yup.object().shape({
        supplyStandardName: yup.string().required('Required'),    
    });

    const handleFormSubmit = useCallback(async (values, onSubmitProps) => {
        try {
            const url = supplyStandardId
                ? `/supplystandards/update/${supplyStandardId}`
                : '/supplystandards/new';

            const method = supplyStandardId ? "put" : "post";

            const allProducts = products.map(product => ({
                _id: product._id,
                quantity: values.products.find(p => p._id === product._id)?.quantity || 0,
            }));

            const payload = {
                ...values,
                products: allProducts, // Submit all products, including those with quantity 0
            };

            await api({
                method,
                url,
                data: payload,
            });

            toast.success(supplyStandardId 
                ? "Supply standard updated successfully." 
                : "Supply standard created successfully.");

            // onSubmitProps.resetForm();
            navigate("/supplystandards");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to save supply standard!");
            setError(error);
        }
    }, [navigate, products, supplyStandardId]);

    const columns = useMemo(() => [
        {
            field: "no",
            headerName: "No",
            filterable: false,      
            sortable: false,
            flex: 1,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,          
        },        
        { 
            field: "productName", 
            headerName: "Product Name", 
            flex: 4,        
        },
        {
            field: "quantity",
            headerName: "Quantity",
            flex: 1.5,
            editable: true,
            type: "number",
            headerAlign: "left",
            align: "left",
        },
    ], []);

    const processRowUpdate = (newRow, oldRow, setFieldValue) => {
        const newQuantity = Number(newRow.quantity);
    
        setFormValues((prevFormValues) => {
            const updatedProducts = prevFormValues.products.map(p =>
                p._id === newRow._id ? { ...p, quantity: newQuantity } : p
            );
    
            const exists = updatedProducts.find(p => p._id === newRow._id);
            if (!exists) updatedProducts.push({ _id: newRow._id, quantity: newQuantity });
    
            setFieldValue("products", updatedProducts);
            return { ...prevFormValues, products: updatedProducts };
        });
    
        return { ...newRow, quantity: newQuantity };
    };     

    const getDisplayRows = () => {
        const productMap = new Map(formValues.products.map(p => [p._id, p.quantity]));
        return products.map(product => ({
            ...product,
            quantity: productMap.get(product._id) || 0, // Display current quantity or 0
        }));
    };

    const handleCopyProducts = useCallback((setFieldValue, multiplier) => {
        if (!selectedSupplyStandardId) return toast.error("Please select a supply standard to copy.");
    
        const sourceStandard = supplyStandards.find(ss => ss._id === selectedSupplyStandardId);
        if (!sourceStandard?.products?.length) return toast.error("No products found in selected supply standard!");
        
        const multipliedProducts = sourceStandard.products.map(product => {            
            return {
                ...product,
                quantity: Math.max(0, Math.floor(product.quantity * multiplier)), 
            }
        });
        
        setFormValues(prev => ({ ...prev, products: multipliedProducts }));
        setFieldValue("products", multipliedProducts);
        toast.success(`Products copied successfully${multiplier > 1 ? ` with multiplier ${multiplier}` : ""}.`);
    }, [selectedSupplyStandardId, supplyStandards]);   
    
    
    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Supply Standard"
                    subtitle={`${loading ? 'Loading...' : supplyStandardId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/supplystandards")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>                    
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={validationSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{
                                        '& > div': { gridColumn: isDesktop ? undefined : 'span 12' },
                                    }}
                                >
                                    <TextField
                                        label="Supply Standard Name"
                                        name="supplyStandardName"
                                        required
                                        variant="filled"
                                        type="text"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.supplyStandardName}
                                        error={!!touched.supplyStandardName && !!errors.supplyStandardName}
                                        helperText={touched.supplyStandardName && errors.supplyStandardName}
                                        sx={{ gridColumn: 'span 6' }}
                                    />

                                    <FormControl variant="filled" sx={{ gridColumn: "span 6" }} >
                                        <InputLabel>Sectors</InputLabel>
                                        <Select
                                            multiple
                                            label="Sectors"
                                            name="sectors"                                            
                                            value={values.sectors || []}
                                            onChange={(e) => setFieldValue("sectors", e.target.value)}
                                            renderValue={(selected) => selected.join(", ")}
                                        >
                                            {airportPairs.map((airportPair, index) => (
                                                <MenuItem 
                                                    key={index} 
                                                    value={airportPair.sector}
                                                >
                                                    {airportPair.sector}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>            

                                    <FormControl variant="filled" sx={{ gridColumn: "span 3" }} >
                                        <InputLabel>Departure</InputLabel>
                                        <Select
                                            multiple
                                            label="Departure Airports"
                                            name="departureAirports"                                            
                                            value={values.departureAirports || []}
                                            onChange={(e) => setFieldValue("departureAirports", e.target.value)}
                                            renderValue={(selected) => selected.join(", ")}
                                        >
                                            {airports.map((airport, index) => (
                                                <MenuItem 
                                                    key={index} 
                                                    value={airport.iataCode}
                                                >
                                                    {airport.iataCode}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl> 

                                    <TextField
                                        label="Minimum Block Time"
                                        name="minBlockTime"
                                        variant="filled"
                                        type="number"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.minBlockTime}
                                        error={!!touched.minBlockTime && !!errors.minBlockTime}
                                        helperText={touched.minBlockTime && errors.minBlockTime}
                                        sx={{ gridColumn: 'span 3' }}
                                        slotProps={{
                                            input: {
                                                onKeyDown: (e) => {
                                                    if (e.key === '-' || e.key === 'e') {
                                                        e.preventDefault();
                                                    }
                                                },
                                            },
                                            htmlInput: { min: 0, step: 0.1 },
                                        }}
                                    />
                                    <TextField
                                        label="Sort Order"
                                        name="sortOrder"
                                        variant="filled"
                                        type="number"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.sortOrder}
                                        error={!!touched.sortOrder && !!errors.sortOrder}
                                        helperText={touched.sortOrder && errors.sortOrder}
                                        slotProps={{
                                            input: {
                                                onKeyDown: (e) => {
                                                    if (e.key === '-' || e.key === 'e') {
                                                        e.preventDefault();
                                                    }
                                                },
                                            },
                                            htmlInput: { min: 0 },
                                        }}
                                        sx={{ gridColumn: 'span 3' }}
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 3' }}
                                        control={
                                            <Checkbox
                                                checked={values.isActive}
                                                onChange={handleChange}
                                                name="isActive"
                                                color="primary"
                                                
                                            />
                                        }
                                        label="Is Active"
                                    />
                                    
                                    {/* Copy Products Section */}
                                    <FormControl variant="filled" sx={{ gridColumn: 'span 4' }}>
                                        <InputLabel>Copy From Supply Standard</InputLabel>
                                        <Select
                                            value={selectedSupplyStandardId}
                                            onChange={(e) => setSelectedSupplyStandardId(e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Select a supply standard</em>
                                            </MenuItem>
                                            {supplyStandards
                                                .filter(ss => ss._id !== supplyStandardId) // Exclude current standard
                                                .map(ss => (
                                                    <MenuItem key={ss._id} value={ss._id}>
                                                        {ss.supplyStandardName}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        label="Multiplier"
                                        name="multiplier"
                                        variant="filled"
                                        type="number"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.multiplier}
                                        error={!!touched.multiplier && !!errors.multiplier}
                                        helperText={touched.multiplier && errors.multiplier}
                                        sx={{ gridColumn: 'span 2' }}
                                        slotProps={{ htmlInput: { min: 1 } }}
                                    />

                                    <Button
                                        fullWidth={isMobile}
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<FileCopyIcon />}
                                        onClick={() => handleCopyProducts(setFieldValue, values.multiplier)}
                                        sx={{ height: 52, gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                    >
                                        Copy Products
                                    </Button>


                                    <DataGrid                    
                                        getRowId={(row) => row._id}
                                        rows={getDisplayRows()}
                                        columns={columns}
                                        processRowUpdate={(newRow, oldRow) => processRowUpdate(newRow, oldRow, setFieldValue)}
                                        onProcessRowUpdateError={(error) => console.error("Row update error:", error)}
                                        rowsPerPageOptions={[25, 50, 100]}
                                        initialState={{
                                            pagination: { paginationModel: { pageSize: 100 } },
                                        }}                   
                                        loading={loading}                             
                                        // showToolbar              
                                        sx={{ gridColumn: 'span 12' }}
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: 'span 12', height: 42 }}
                                    >
                                        {supplyStandardId ? 'Update' : 'Create'}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default SupplystandardPage;
